import { updateDocument } from '@/pages/api/scribeAPI';
import { toast } from '@heidi/design-system/toast';

import {
  type DocumentUpdateAttempt,
  trackDocumentUpdateAttempt,
} from '@/lib/telemetry/documentUpdateTracking';
import { type DocumentResponse } from '@/types';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@heidi/runtime/logger';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import { ContentType, DocumentGenType } from '@heidi/schema/ml-scribe';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useSessionEhrProvider } from '../integrations/useSessionEhrProvider';
import { GET_DOCUMENT_BY_ID } from '../query/useQueryDocumentById';
import { GET_DOCUMENTS_BY_ID } from '../query/useQueryDocumentsBySessionId';
import { useEnableBlockEditorV1 } from '../useEnableBlockEditorV1';

type Props = {
  content: string;
  sessionId: string;
  documentId: string;
  /** Set by an autosave cycle: the request emits a `document_update` attempt. */
  autosaveAttempt?: DocumentUpdateAttempt;
};

export const UPDATE_DOCUMENT_BY_ID = 'UPDATE_DOCUMENT_BY_ID';

/**
 * Persists document edits to the server.
 *
 * @param options.contentTypeOverride - Bypass the `useEnableBlockEditorV1`-
 *   derived `content_type` and label the saved document with the caller-supplied
 *   type. Required for the markdown-native editor, which always emits markdown
 *   regardless of the user-cohort block-editor flag; without this, markdown
 *   payloads would be saved as `ContentType.HTML` when `release-block-editor-v1`
 *   is enabled, corrupting the document on the next load.
 */
export function useMutateUpdateSyncDocumentById(options?: {
  contentTypeOverride?: ContentType;
}) {
  const queryClient = useQueryClient();

  // Resolve the block-editor decision from the ACTIVE session's provider so the
  // saved content_type matches the editor the user is actually in. Without this
  // the save falls back to the first completed onboarding session and, for a
  // multi-integration user, can label a Markdown document as HTML (corrupting
  // it on the next load). Off the session page useCurrentSessionId() is '' →
  // prior fallback.
  const enableBlockEditorV1 = useEnableBlockEditorV1(
    useSessionEhrProvider(useCurrentSessionId()).sessionEhrProvider
  );
  const resolvedContentType =
    options?.contentTypeOverride ??
    (enableBlockEditorV1 ? ContentType.HTML : ContentType.MARKDOWN);

  return useMutation({
    mutationKey: [UPDATE_DOCUMENT_BY_ID],
    mutationFn: async ({ content, sessionId, documentId, autosaveAttempt }: Props) => {
      const document = queryClient.getQueryData<DocumentResponse>([
        GET_DOCUMENT_BY_ID,
        { sessionId, documentId },
      ]);

      const save = () =>
        updateDocument({
          sessionId,
          documentId,
          name: document?.name || '',
          content,
          generationType: document?.generation_type || DocumentGenType.TEMPLATE,
          updatedDictation: document?.updated_dictation || '',
          contentType: resolvedContentType,
        });

      return autosaveAttempt ? trackDocumentUpdateAttempt(autosaveAttempt, save) : save();
    },
    onMutate: async ({ sessionId, documentId, content }) => {
      await queryClient.cancelQueries({
        queryKey: [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
      });

      const previousDocument = queryClient.getQueryData<DocumentResponse>([
        GET_DOCUMENT_BY_ID,
        { sessionId, documentId },
      ]);

      queryClient.setQueryData<DocumentResponse>(
        [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
        (oldData) => {
          if (!oldData) {
            return undefined;
          }

          return produce(oldData, (draft) => {
            draft.content = content;
            draft.content_type = resolvedContentType;
          });
        }
      );

      return { previousDocument };
    },
    onSuccess: async (_, { sessionId, documentId }) => {
      await queryClient.invalidateQueries({
        queryKey: [GET_DOCUMENT_BY_ID, { sessionId, documentId }],
      });

      await queryClient.invalidateQueries({
        queryKey: [GET_DOCUMENTS_BY_ID, { sessionId }],
      });
    },
    onError: (error, { sessionId, documentId }) => {
      // Raw error leaks `config.data` — the document body — into Datadog.
      logger.error('Failed to update document', {
        session_id: sessionId,
        document_id: documentId,
        error: sanitizeError(error),
      });
      toast({
        tone: 'destructive',
        title: 'Failed to update document',
        description: error.message,
      });
    },
  });
}
